import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock, User, MessageSquare, Globe, Facebook, Instagram } from 'lucide-react';
import { BsPinterest } from 'react-icons/bs';
import { apiService, ContactData } from '../services/api';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const contactData: ContactData = {
        fullName: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        phone: formData.phone
      };

      const result = await apiService.submitContactForm(contactData);
      
      if (result.success) {
        setSuccess(result.message);
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          phone: ''
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
      {/* Header Section */}
      <section className="py-12 bg-gradient-to-r from-dpn-light-green to-dpn-olive">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="font-runtime text-4xl md:text-5xl font-bold mb-4">Get in Touch</h1>
            <p className="font-lucy-rose text-xl opacity-95 mx-auto">
              We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-runtime text-3xl md:text-4xl font-bold text-black mb-4">Contact Information</h2>
            <p className="font-lucy-rose text-lg text-gray-600 max-w-2xl mx-auto">
              Multiple ways to reach our team for support, partnerships, or general inquiries
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Email */}
            <div className="text-center p-6 rounded-xl bg-gray-50 hover:bg-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-center mb-4">
                <Mail className="h-6 w-6 text-dpn-pink" />
              </div>
              <h3 className="font-runtime text-xl font-bold text-black mb-2">Email Us</h3>
              <p className="font-lucy-rose text-lg text-gray-800 font-medium mb-2">hello@DurgaPuja.Network</p>
              <p className="font-lucy-rose text-sm text-gray-600">Send us an email and we'll respond within 24 hours</p>
            </div>

            {/* Phone */}
            <div className="text-center p-6 rounded-xl bg-gray-50 hover:bg-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-center mb-4">
                <Phone className="h-6 w-6 text-dpn-light-green" />
              </div>
              <h3 className="font-runtime text-xl font-bold text-black mb-2">Call Us</h3>
              <p className="font-lucy-rose text-lg text-gray-800 font-medium mb-2">+91 9810693616</p>
              <p className="font-lucy-rose text-sm text-gray-600">Available Monday to Friday, 9 AM to 6 PM IST</p>
            </div>

            {/* Location */}
            <div className="text-center p-6 rounded-xl bg-gray-50 hover:bg-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-center mb-4">
                <MapPin className="h-6 w-6 text-dpn-olive" />
              </div>
              <h3 className="font-runtime text-xl font-bold text-black mb-2">Visit Us</h3>
              <p className="font-lucy-rose text-lg text-gray-800 font-medium mb-2">Kolkata, West Bengal, India</p>
              <p className="font-lucy-rose text-sm text-gray-600">Ground Floor, Ghosh Villa, 8/K/1 Baishnabghata Bye Lane P. O. Naktala, P.S. Netaji Nagar, Kolkata Pin Code- 700047</p>
            </div>

            {/* Business Hours */}
            <div className="text-center p-6 rounded-xl bg-gray-50 hover:bg-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-center mb-4">
                <Clock className="h-6 w-6 text-dpn-raspberry" />
              </div>
              <h3 className="font-runtime text-xl font-bold text-black mb-2">Business Hours</h3>
              <p className="font-lucy-rose text-lg text-gray-800 font-medium mb-2">Mon - Fri: 9 AM - 6 PM IST</p>
              <p className="font-lucy-rose text-sm text-gray-600">Weekend support available for urgent matters</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="px-8 py-12">
              <div className="text-center mb-8">
                <h2 className="font-runtime text-3xl md:text-4xl font-bold text-black mb-4">Send Us a Message</h2>
                <p className="font-lucy-rose text-lg text-gray-600">
                  Fill out the form below and we'll get back to you as soon as possible
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                      Full Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-light-green focus:border-transparent outline-none font-lucy-rose"
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-light-green focus:border-transparent outline-none font-lucy-rose"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-light-green focus:border-transparent outline-none font-lucy-rose"
                      placeholder="+919876543210"
                    />
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                    Subject *
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <select
                      id="subject"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-light-green focus:border-transparent outline-none font-lucy-rose"
                    >
                      <option value="">Select a department</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Committee Support">Committee Support</option>
                      <option value="Technical Support">Technical Support</option>
                      <option value="Partnership & Collaboration">Partnership & Collaboration</option>
                      <option value="Media & Press">Media & Press</option>
                      <option value="Cultural Documentation">Cultural Documentation</option>
                      <option value="Volunteer Opportunities">Volunteer Opportunities</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                    Message *
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      required
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-light-green focus:border-transparent outline-none font-lucy-rose resize-none"
                      placeholder="Tell us how we can help you..."
                    />
                  </div>
                </div>

                {/* Success Message */}
                {success && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-green-800 font-lucy-rose">{success}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-red-800 font-lucy-rose">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-dpn-light-green hover:bg-dpn-olive text-white font-lucy-rose font-semibold py-4 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Send className="mr-2 h-5 w-5" />
                  )}
                  {loading ? 'Sending Message...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media & Company Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Social Media */}
            <div className="text-center lg:text-left">
              <h3 className="font-runtime text-2xl font-bold text-black mb-6">Follow Us on Social Media</h3>
              <p className="font-lucy-rose text-gray-600 mb-8">
                Stay connected with our community and get the latest updates on Durga Puja celebrations worldwide.
              </p>
              <div className="flex justify-center lg:justify-start space-x-4">
                <a
                  href="https://www.facebook.com/profile.php?id=61578083672940"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-gray-100 hover:bg-dpn-pink hover:text-white rounded-full transition-all duration-200 transform hover:scale-110"
                  aria-label="Facebook"
                >
                  <Facebook className="h-6 w-6" />
                </a>

                <a
                  href="https://www.instagram.com/durgapujanetwork/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-gray-100 hover:bg-dpn-pink hover:text-white rounded-full transition-all duration-200 transform hover:scale-110"
                  aria-label="Instagram"
                >
                  <Instagram className="h-6 w-6" />
                </a>
                <a
                  href="https://in.pinterest.com/durgapujanetwork/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-gray-100 hover:bg-dpn-pink hover:text-white rounded-full transition-all duration-200 transform hover:scale-110"
                  aria-label="Pinterest"
                >
                  <BsPinterest className="h-6 w-6" />
                </a>
              </div>
            </div>

            {/* Company Information */}
            <div className="text-center lg:text-left">
              <h3 className="font-runtime text-2xl font-bold text-black mb-6">About Our Organization</h3>
              <div className="space-y-4 font-lucy-rose text-gray-600">
                <p>
                  <strong className="text-black">Durga Puja Network</strong>, a global Durga Puja archive, to help you connect, share and celebrate world's most vibrant cultural festival through art, community and heritage. We celebrate the vibrant community spirit that makes this Durga Puja an UNESCO-recognized Global Cultural Heritage. Dugga Dugga !
                </p>
                <p><strong className="text-black">Company:</strong> Mind's Workstation India Pvt. Ltd.</p>
                <p><strong className="text-black">CIN:</strong> U74999WB2022PTC250672</p>
                <p><strong className="text-black">GST:</strong> 19AAPCM3337G1Z6</p>
                <p><strong className="text-black">Start-up India Reg No:</strong> DIPP115871</p>
                <p><strong className="text-black">Registered Office:</strong> Ground Floor, Ghosh Villa, 8/K/1 Baishnabghata Bye Lane P. O. Naktala, P.S. Netaji Nagar, Kolkata Pin Code- 700047</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Contact Section */}
      <section className="py-12 bg-gradient-to-r from-dpn-raspberry to-dpn-pink">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-runtime text-2xl font-bold text-white mb-4">Need Immediate Assistance?</h3>
          <p className="font-lucy-rose text-white/95 mb-6">
            For urgent matters during Durga Puja season or critical technical issues
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                const contactForm = document.querySelector('form') || document.getElementById('contact-form');
                if (contactForm) {
                  contactForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="bg-white text-dpn-raspberry hover:bg-gray-100 font-lucy-rose font-semibold px-6 py-3 rounded-full transition-colors duration-200"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
